import config from "../config";

/**
 * Appelle l'endpoint de la Cloud Function pour récupérer les détails d'une caution.
 *
 * @param {string} depositId - L'identifiant de la caution.
 * @returns {Promise<Object>} - Un objet contenant les détails de la caution.
 * @throws {Error} - En cas d'erreur HTTP ou de problème lors de l'appel.
 */
export async function getDepositDetails(depositId) {
  const endpointURL = `${config.API_BASE_URL}/getDepositDetails`;

  const response = await fetch(endpointURL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ depositId })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}


/**
 * Appelle la fonction Cloud "createDeposit" pour créer un dépôt.
 * @param {string} ownerEmail - Votre adresse e-mail.
 * @param {number|string} depositAmount - Le montant de la caution.
 * @param {string} clientEmail - L'adresse e-mail du client.
 * @returns {Promise<Object>} - L'objet retourné par la Cloud Function, incluant depositId.
 */
export async function createDeposit(ownerEmail, depositAmount, ownerClientEmail, captchaToken) {
  const endpointURL = `${config.API_BASE_URL}/createDeposit`;
  const response = await fetch(endpointURL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ownerEmail, depositAmount, ownerClientEmail, captchaToken })
  });
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }
  return await response.json();
}

/**
 * Appelle l'endpoint Cloud Function "releaseDeposit" pour libérer la caution.
 *
 * @param {string} depositId - L'identifiant du dépôt.
 * @param {string} token - Le token de sécurité (JWT) associé au dépôt.
 * @returns {Promise<Object>} La réponse JSON de l'endpoint.
 * @throws {Error} Si la requête échoue.
 */
export async function releaseDeposit(depositId, token) {
  const endpointURL = `${config.API_BASE_URL}/releaseDeposit`;
  const response = await fetch(endpointURL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ depositId, token })
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `HTTP error: ${response.status}`);
  }
  return await response.json();
}

/**
 * Appelle l'endpoint pour collecter la caution.
 *
 * @param {string} depositId - L'identifiant du dépôt.
 * @param {string} token - Le token JWT de validation.
 * @param {number} amount - Le montant (en euros) à collecter.
 * @returns {Promise<Object>} La réponse de l'API.
 */
export async function collectDeposit(depositId, token, amount) {
  const endpointURL = `${config.API_BASE_URL}/collectDeposit`;
  const response = await fetch(endpointURL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ depositId, token, amount }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }
  return await response.json();
}